import * as React from 'react';

import { Message } from '@longline/aqua-ui/containers/Message';
import { View } from '@longline/aqua-ui/controls/View';
import { SecondaryButton } from '@longline/aqua-ui/controls/SecondaryButton';
import { SVG } from '@longline/aqua-ui/svg';
import { TabBar } from '@longline/aqua-ui/controls/TabBar'
import { CountryFormatter } from '@longline/aqua-ui/formatters/CountryFormatter';
import { DateTimeFormatter } from '@longline/aqua-ui/formatters/DateTimeFormatter';

import { useUsersContext } from '../IUsersContext';
import { useConfirm } from '../../../contexts/confirm/ConfirmContext';
import { Pane } from '../../../ui/Pane';
import { SystemListView } from '../SystemListView';
import { ISystemFilter, SystemFilter } from "../../../filters/SystemFilter";
import { Can } from '../../../ui/Can';
import { Grid } from '../../../ui/Grid';
import { Role } from '../../../api/Permission';
import { RoleBox } from '../../../ui/RoleBox';

const ViewUser = () => {
  const ctx = useUsersContext();
  const confirmCtx = useConfirm();

  const [filter, setFilter] = React.useState<ISystemFilter>({});

  React.useEffect(() => {
    if (ctx.user) ctx.loadUser(ctx.user.uuid);
  }, []);

  // The mode === 'view' switch in UsersManager guarantees ctx.user is
  // populated before this component is rendered.
  if (!ctx.user) return null;
  const user = ctx.user;

  const handleDelete = () => {
    ctx.deleteUser()
  }

  const renderUserData = () => {
    if(ctx.userReady == 'failed') {
      return <Message.Error onRetry={() => ctx.loadUser(user.uuid)}>Failed to load user.</Message.Error>;
    }

    return (
      <Grid>
        <Grid.Item weight={6}>
          <View 
            ghost={ctx.userReady == 'loading'} 
            label="email" 
            hint="Users email address">
              {user.email}
          </View>
        </Grid.Item>
        <Grid.Item weight={6}>
          <View
            ghost={ctx.userReady == 'loading'}
            label="Name"
            hint="User name">
              {user.name || '\u2014'}
          </View>
        </Grid.Item>
        <Grid.Item weight={6}>
          <View ghost={ctx.userReady == 'loading'}
          label="Country"
          hint="User home country">
            {user.country ? <CountryFormatter type="both" value={user.country}/> : "\u2014"}
          </View>
        </Grid.Item>
        <Grid.Item weight={6}>
          <View
            ghost={ctx.userReady == 'loading'}
            label="Phone number"
            hint="Mobile number for SMS login">
              {user.phoneNumber || '\u2014'}
          </View>
        </Grid.Item>
        <Grid.Item weight={6}>
          <View
            ghost={ctx.userReady == 'loading'}
            label="Roles"
            hint="User roles"
          >
            <div style={{display: 'flex', gap: '8px', }}>
              {(user.roles || []).filter(r => r !== Role.User).length == 0 && "\u2014"}
              {(user.roles || []).filter(r => r !== Role.User).map((r, idx) => <RoleBox key={idx} role={r} full/>)}
            </div>                
          </View>
        </Grid.Item>
        <Grid.Item weight={6}>
          <View ghost={ctx.userReady == 'loading'} label="Created" hint="User creation date/time">
            <DateTimeFormatter.LongDateTime value={user.created_at ?? ""}/>
          </View>
        </Grid.Item>
        <Grid.Item weight={6}>
          <View ghost={ctx.userReady == 'loading'} label="Updated" hint="User last update/time">
            <DateTimeFormatter.LongDateTime value={user.updated_at ?? ""}/>
          </View>
        </Grid.Item>        
      </Grid>
    )
  }

  return (
    <>
      <Pane.Heading>
        {ctx.tab == 'data' && <Pane.Title>{user.name}</Pane.Title>}
        {ctx.tab == 'systems' && <SystemFilter filter={filter} onChange={setFilter}/>}
      </Pane.Heading>
      <Pane.Subheader expandable action={
        <Can permission="manage_users">
          <SecondaryButton title="Delete user" negative icon={SVG.Icons.Delete} onClick={() => confirmCtx.start(handleDelete, "Delete user?", "Are you sure you want to delete this user?")}/>
        </Can>}>
        <Can permission="manage_users">DELETE USER</Can>
      </Pane.Subheader>
      
      {/* Tab bar allows switching between user data view or system list view. */}
      <TabBar active={ctx.tab === 'systems' ? 1 : 0} line>
        <TabBar.Tab onClick={() => ctx.setTab('data')}>Data</TabBar.Tab>
        <TabBar.Tab onClick={() => ctx.setTab('systems')}>Systems</TabBar.Tab>
      </TabBar>

      {/* User data view */}
      {ctx.tab === 'data' && 
        <Pane.Body>
          {renderUserData()}
        </Pane.Body>}

      {/* System list */}
      {ctx.tab == 'systems' && 
        <SystemListView loading={ctx.userReady === 'loading'} systems={user.systems} filter={filter} onClearFilter={() => setFilter({})} />}

      <Pane.Footer>
        <Pane.CancelButton onClick={() => { ctx.setTab('data'); ctx.setMode('list'); }}/>
        <Can permission="manage_users">
          <Pane.EditButton disabled={ctx.userReady == 'loading'} onClick={() => ctx.setMode('edit')}/>
        </Can>
      </Pane.Footer>      
    </>
  )
}

export { ViewUser }
